import React from "react";
import { NavLink } from "react-router-dom";
import {
  FaBuilding,
  FaChartLine,
  FaChalkboardTeacher,
  FaCreditCard,
  FaGraduationCap,
  FaImage,
  FaUser,
} from "react-icons/fa";
import { useTenant } from "../../context/TenantContext";
import { SUBSCRIPTION_PLANS } from "../../data/subscriptionPlans";
import { normalizeRole, ROLE_ADMIN, ROLE_SCHOOL, ROLE_TEACHER, ROLE_STAFF, STUDENT_ROLES } from "../../utils/authRole";

const TuitionSidebar = ({ role }) => {
  const normalizedRole = normalizeRole(role);
  const { activeTenant, tenantLogoUrl } = useTenant();
  const subscription = activeTenant?.subscription;
  const plan = String(subscription?.plan || activeTenant?.plan || "FREE_TRIAL").toUpperCase();
  const isPaid = subscription?.status === "ACTIVE" && !["FREE", "TRIAL", "FREE_TRIAL", "UNPAID"].includes(plan);
  const useTenantBranding = isPaid && activeTenant?.brandingEnabled !== false;
  const sharedPlan = SUBSCRIPTION_PLANS.find((candidate) => candidate.value === plan);
  const planLabel = isPaid ? (sharedPlan?.name || plan) : "ClassNX free trial";
  const displayName = useTenantBranding
    ? (activeTenant?.name || activeTenant?.schoolName || "Tuition Workspace")
    : "ClassNX";
  const logo = useTenantBranding
    ? (tenantLogoUrl || activeTenant?.logoUrl || activeTenant?.schoolLogoUrl || "")
    : "";

  const owner = [ROLE_ADMIN, ROLE_SCHOOL].includes(normalizedRole);
  const teacher = [ROLE_TEACHER, ROLE_STAFF].includes(normalizedRole);
  const student = STUDENT_ROLES.includes(normalizedRole);
  const links = [
    owner && { to: "/tuition/dashboard", label: "Tuition Dashboard", icon: FaChartLine },
    teacher && { to: "/tuition/teacher", label: "Faculty Workspace", icon: FaChalkboardTeacher },
    student && { to: "/tuition/student", label: "Student Portal", icon: FaGraduationCap },
    owner && { to: "/subscription-plans", label: "Plans & Billing", icon: FaCreditCard },
    owner && isPaid && { to: "/settings/branding", label: "Branding", icon: FaImage },
    { to: "/profile", label: "My Profile", icon: FaUser },
  ].filter(Boolean);

  return (
    <aside className="fixed bottom-0 left-0 top-0 z-[60] hidden w-[248px] flex-col border-r border-slate-800 bg-slate-950 text-white lg:flex">
      <div className="border-b border-slate-800 p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-sky-600 text-xl shadow-lg shadow-sky-950/40">
            {logo ? <img src={logo} alt={`${displayName} logo`} className="h-full w-full object-contain bg-white" /> : <FaBuilding />}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-black">{displayName}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-sky-400">Tuition workspace</p>
          </div>
        </div>
        <span className="mt-3 inline-flex rounded-full bg-slate-800 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-300">
          {isPaid ? `${planLabel} plan` : planLabel}
        </span>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-3" aria-label="Tuition workspace">
        {links.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold transition ${isActive ? "bg-sky-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}
          >
            <Icon className="shrink-0" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-4 text-[10px] leading-4 text-slate-500">
        {useTenantBranding ? "Powered by ClassNX" : "ClassNX Tuition Management"}
      </div>
    </aside>
  );
};

export default TuitionSidebar;
